import { routes, type Technology, technologyInputSchema } from '@pmai/contracts';
import {
  Alert,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  FileButton,
  FormDialog,
  IconButton,
  PageHeader,
  Spinner,
  TextField,
} from '@pmai/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TechLogo } from '../../entities/technologies/tech-logo';
import type { ApiError } from '../../shared/api/client';
import { useApiMutation, useApiQuery } from '../../shared/api/hooks';
import { Can } from '../../shared/auth/session';
import { bind, useZodForm } from '../../shared/forms/form';
import { useErrorText } from '../../shared/i18n';
import { toSquarePng } from '../../shared/image';
import { useConfirm } from '../../shared/use-confirm';

const nameSchema = technologyInputSchema.pick({ name: true });
const IMAGES = 'image/*';
const invalidates = [routes.technologies.list];

/**
 * Picture file → 60×60 PNG handed to `use` (FR-TECH-003/005); `error` is set when the file is
 * not an image.
 */
function useLogoFile() {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const pick = (file: File, use: (logo: string) => void) => {
    setError(null);
    toSquarePng(file).then(use, () => setError(t('technologies.invalidImage')));
  };
  return { pick, error, clear: () => setError(null) };
}

/** A taken name (CONFLICT) belongs to the name field; everything else to the form. */
function useNameError() {
  const { t } = useTranslation();
  const errorText = useErrorText();
  return (error: ApiError | null) =>
    error && (error.code === 'CONFLICT' ? t('technologies.nameTaken') : errorText(error));
}

/** Technology / product catalogue as cards (FR-TECH-001…006). */
export function TechnologiesPage() {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const technologies = useApiQuery(routes.technologies.list);
  const [renaming, setRenaming] = useState<Technology | null>(null);
  const update = useApiMutation(routes.technologies.update, { invalidates });
  const logo = useLogoFile();
  const remove = useConfirm(routes.technologies.remove, {
    invalidates,
    params: (tech: Technology) => ({ id: tech.id }),
  });
  const pageError = logo.error ?? (update.error ? errorText(update.error) : null);

  return (
    <>
      <PageHeader title={t('technologies.title')} />
      <div className="flex flex-col gap-5">
        <Can perm="technologies.edit">
          <AddTechnology />
        </Can>
        {pageError && <Alert tone="danger">{pageError}</Alert>}
        {technologies.isPending ? (
          <Spinner label={t('common.loading')} />
        ) : !technologies.data?.length ? (
          <EmptyState icon="puzzle" title={t('technologies.empty')} />
        ) : (
          <ul
            aria-label={t('technologies.title')}
            className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          >
            {technologies.data.map((tech) => (
              <li
                key={tech.id}
                className="flex items-center gap-3 rounded-card border border-border bg-surface p-3 shadow-sm"
              >
                <TechLogo logo={tech.logo} />
                <h2 className="min-w-0 flex-1 break-words text-sm font-semibold">{tech.name}</h2>
                <Can perm="technologies.edit">
                  <div className="flex gap-1">
                    <IconButton
                      icon="pencil"
                      label={`${t('technologies.rename')}: ${tech.name}`}
                      onClick={() => setRenaming(tech)}
                    />
                    <FileButton
                      accept={IMAGES}
                      variant="ghost"
                      icon="image-plus"
                      label={`${t('technologies.changeLogo')}: ${tech.name}`}
                      onFile={(file) => {
                        update.reset();
                        logo.pick(file, (png) =>
                          update.mutate({ params: { id: tech.id }, body: { logo: png } }),
                        );
                      }}
                    />
                    <IconButton
                      icon="trash-2"
                      label={`${t('common.delete')}: ${tech.name}`}
                      onClick={() => remove.ask(tech)}
                    />
                  </div>
                </Can>
              </li>
            ))}
          </ul>
        )}
      </div>
      <RenameDialog technology={renaming} onClose={() => setRenaming(null)} />
      <ConfirmDialog
        {...remove.dialog}
        title={t('technologies.deleteTitle')}
        message={t('technologies.deleteText', { name: remove.target?.name ?? '' })}
        confirmLabel={t('common.delete')}
      />
    </>
  );
}

/** Name (required, Enter adds) and an optional logo with preview (FR-TECH-002/003). */
function AddTechnology() {
  const { t } = useTranslation();
  const nameError = useNameError();
  const [pending, setPending] = useState<string | null>(null);
  const logo = useLogoFile();
  const form = useZodForm(nameSchema, { defaultValues: { name: '' } });
  const create = useApiMutation(routes.technologies.create, {
    invalidates,
    onSuccess: () => {
      form.reset({ name: '' });
      setPending(null);
      logo.clear();
    },
  });
  const field = bind(form, 'name');
  const serverError = create.error ? nameError(create.error) : null;

  return (
    <Card>
      <form
        noValidate
        className="flex flex-wrap items-end gap-3"
        onSubmit={form.handleSubmit((v) => create.mutate({ body: { name: v.name, logo: pending } }))}
      >
        <div className="flex items-center gap-2">
          {pending ? (
            <>
              <img src={pending} alt={t('technologies.logoPreview')} className="size-[60px] rounded" />
              <IconButton icon="x" label={t('technologies.removeLogo')} onClick={() => setPending(null)} />
            </>
          ) : (
            <FileButton
              accept={IMAGES}
              icon="image-plus"
              label={t('technologies.pickLogo')}
              onFile={(file) => logo.pick(file, setPending)}
            />
          )}
        </div>
        <TextField
          label={t('technologies.name')}
          required
          className="min-w-56 flex-1"
          {...field}
          error={field.error ?? serverError ?? undefined}
        />
        <Button type="submit" icon="plus" loading={create.isPending}>
          {t('technologies.add')}
        </Button>
      </form>
      {logo.error && (
        <div className="mt-3">
          <Alert tone="danger">{logo.error}</Alert>
        </div>
      )}
    </Card>
  );
}

/** ✏️ — rename in a dialog with the current name (FR-TECH-004). */
function RenameDialog({ technology, onClose }: { technology: Technology | null; onClose: () => void }) {
  const { t } = useTranslation();
  const nameError = useNameError();
  const form = useZodForm(nameSchema, { defaultValues: { name: '' } });
  const update = useApiMutation(routes.technologies.update, { invalidates, onSuccess: onClose });
  const { reset } = form;

  useEffect(() => {
    if (!technology) return;
    update.reset();
    reset({ name: technology.name });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when the dialog opens for another item
  }, [technology, reset]);

  return (
    <FormDialog
      open={technology !== null}
      onOpenChange={(o) => !o && onClose()}
      title={t('technologies.renameTitle')}
      submitting={update.isPending}
      error={nameError(update.error)}
      onSubmit={form.handleSubmit(
        (v) => technology && update.mutate({ params: { id: technology.id }, body: { name: v.name } }),
      )}
    >
      <TextField label={t('technologies.name')} required {...bind(form, 'name')} />
    </FormDialog>
  );
}
